import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import type { User, AuthTokens } from '@/types/auth'
import { STORAGE_KEYS } from '@/lib/constants'
import { safeLocalStorage } from '@/lib/safeStorage'
import { clearActAs } from '@/features/facility-manager/actAs'

/** JWT 토큰에서 만료 시간 추출 */
function isTokenExpired(token: string | null): boolean {
  if (!token) return true
  try {
    const parts = token.split('.')
    const payload = JSON.parse(atob(parts[1] ?? ''))
    return payload.exp * 1000 < Date.now()
  } catch {
    return true
  }
}

/** 인증 상태 인터페이스 */
interface AuthState {
  /** 액세스 토큰 */
  accessToken: string | null
  /** 리프레시 토큰 */
  refreshToken: string | null
  /** 사용자 정보 */
  user: User | null
  /** 인증 여부 */
  isAuthenticated: boolean
}

/** 인증 액션 인터페이스 */
interface AuthActions {
  /** 로그인 처리 (사용자 정보 + 토큰 저장) */
  login: (user: User, tokens: AuthTokens) => void
  /** 로그아웃 처리 (모든 상태 초기화) */
  logout: () => void
  /** 토큰 업데이트 */
  setTokens: (tokens: AuthTokens) => void
  /** 사용자 정보 업데이트 */
  setUser: (user: User) => void
}

type AuthStore = AuthState & AuthActions

/** 초기 상태 */
const initialState: AuthState = {
  accessToken: null,
  refreshToken: null,
  user: null,
  isAuthenticated: false,
}

/**
 * Zustand 인증 스토어
 *
 * - persist 미들웨어로 localStorage에 토큰/사용자 정보 저장
 * - Phase 2에서 HttpOnly Cookie 방식으로 전환 예정
 */
export const useAuthStore = create<AuthStore>()(
  persist(
    (set) => ({
      ...initialState,

      login: (user, tokens) => {
        // localStorage에도 별도 저장 (axios 인터셉터에서 사용)
        safeLocalStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, tokens.accessToken)
        safeLocalStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, tokens.refreshToken)

        set({
          accessToken: tokens.accessToken,
          refreshToken: tokens.refreshToken,
          user,
          isAuthenticated: true,
        })
      },

      logout: () => {
        safeLocalStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN)
        safeLocalStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN)
        safeLocalStorage.removeItem(STORAGE_KEYS.USER)
        // 운영자 대신 처리 대상도 함께 정리 (다음 로그인 사용자에게 남지 않게) — admin-act-as.md §6.1
        clearActAs()

        set(initialState)
      },

      setTokens: (tokens) => {
        safeLocalStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, tokens.accessToken)
        safeLocalStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, tokens.refreshToken)

        set({
          accessToken: tokens.accessToken,
          refreshToken: tokens.refreshToken,
        })
      },

      setUser: (user) => {
        set({ user })
      },
    }),
    {
      name: 'carebalance-auth',
      // localStorage 접근이 SecurityError 를 던지는 환경(시크릿/인앱 WebView 등)에서도
      // persist 미들웨어가 깨지지 않도록 안전 wrapper 사용. (참조: lib/safeStorage.ts)
      storage: createJSONStorage(() => safeLocalStorage),
      // SSR 호환: 자동 hydration 차단. 클라이언트 entry에서 명시적으로 rehydrate.
      skipHydration: true,
      // isAuthenticated 는 token 유효성으로부터 파생되므로 저장하지 않는다.
      // (저장 → 복원 시 mutation 으로는 반영이 안 되고 stale 한 true 가 유지되어
      //  401 → refresh → 401 노이즈가 콘솔에 남는 문제가 있었음)
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        user: state.user,
      }),
      // 복원 시 토큰 상태로 isAuthenticated 재계산. 둘 다 만료면 완전 초기화.
      merge: (persisted, current) => {
        const p = (persisted ?? {}) as Partial<AuthState>
        const accessToken = p.accessToken ?? null
        const refreshToken = p.refreshToken ?? null
        const accessValid = !!accessToken && !isTokenExpired(accessToken)
        const refreshValid = !!refreshToken && !isTokenExpired(refreshToken)
        if (!accessValid && !refreshValid) {
          // 둘 다 무효 → 완전 로그아웃 상태로 복원 (localStorage 도 정리)
          safeLocalStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN)
          safeLocalStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN)
          safeLocalStorage.removeItem(STORAGE_KEYS.USER)
          return { ...current, ...initialState }
        }
        // access 유효 → 바로 사용. access 만료 + refresh 유효 → isAuthenticated 유지,
        // 이후 첫 API 호출 시 interceptor 가 자동 갱신.
        return {
          ...current,
          accessToken,
          refreshToken,
          user: p.user ?? null,
          isAuthenticated: true,
        }
      },
    }
  )
)
